import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Layout4 from "../DataLayouts/Layout4";
import {
  createApplicationUser,
  getApplicationUser,
  listApplicationRoles,
  listRoleAssignments,
  syncUserRoleAssignment,
  updateApplicationUser,
} from "../../Apis/ApplicationAdminUserApis";

const emptyForm = {
  full_name: "",
  email: "",
  country_phone_code: "+91",
  phone_number: "",
  password_hash: "",
  is_active: true,
  application_user_role_id: "",
  application_user_role_assignment_id: "",
};

export default function ApplicationUserForm({ mode = "add" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isView = mode === "view";
  const isEdit = mode === "edit";

  const [form, setForm] = useState(emptyForm);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(isEdit || isView);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listApplicationRoles()
      .then(setRoles)
      .catch((err) => toast.error(err.message || "Failed to load roles"));
  }, []);

  useEffect(() => {
    if (!(isEdit || isView) || !id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [user, assignments] = await Promise.all([
          getApplicationUser(id),
          listRoleAssignments(),
        ]);
        if (cancelled || !user) return;

        const match = (assignments || []).find(
          (a) => Number(a.application_user_id) === Number(id)
        );

        setForm({
          full_name: user.full_name || "",
          email: user.email || "",
          country_phone_code: user.country_phone_code || "+91",
          phone_number: user.phone_number || "",
          password_hash: "",
          is_active: Boolean(user.is_active),
          application_user_role_id: match?.application_user_role_id
            ? String(match.application_user_role_id)
            : "",
          application_user_role_assignment_id:
            match?.application_user_role_assignment_id
              ? String(match.application_user_role_assignment_id)
              : "",
        });
      } catch (err) {
        toast.error(err.message || "Failed to load user");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, isView]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) return;

    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    if (!isEdit && !form.password_hash) {
      toast.error("Password is required.");
      return;
    }
    if (!form.phone_number || !form.country_phone_code) {
      toast.error("Phone is required.");
      return;
    }

    setSaving(true);
    try {
      let userId = id ? Number(id) : null;
      if (isEdit) {
        const payload = {
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          country_phone_code: form.country_phone_code.trim(),
          phone_number: form.phone_number.trim(),
          is_active: form.is_active,
        };
        if (form.password_hash) {
          payload.password_hash = form.password_hash;
        }
        await updateApplicationUser(userId, payload);
      } else {
        const created = await createApplicationUser({
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          country_phone_code: form.country_phone_code.trim(),
          phone_number: form.phone_number.trim(),
          password_hash: form.password_hash,
          is_active: form.is_active,
        });
        userId = created?.application_user_id;
      }

      if (userId) {
        await syncUserRoleAssignment(
          userId,
          form.application_user_role_id
            ? Number(form.application_user_role_id)
            : null,
          form.application_user_role_assignment_id
            ? Number(form.application_user_role_assignment_id)
            : null
        );
      }

      toast.success(isEdit ? "User updated" : "User created");
      navigate("/users");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const title = isView ? "View user" : isEdit ? "Edit user" : "Add user";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720 }}>
      <Layout4
        heading={title}
        subtitle="Application identity and role"
        showAddButton={false}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Full name"
              name="full_name"
              value={form.full_name}
              onChange={onChange}
              disabled={isView}
              required
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              disabled={isView}
              required
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Country code"
                name="country_phone_code"
                value={form.country_phone_code}
                onChange={onChange}
                disabled={isView}
                required
                sx={{ width: { sm: 160 } }}
              />
              <TextField
                label="Phone number"
                name="phone_number"
                value={form.phone_number}
                onChange={onChange}
                disabled={isView}
                required
                fullWidth
              />
            </Stack>
            {!isView && (
              <TextField
                label={isEdit ? "New password (optional)" : "Password"}
                name="password_hash"
                type="password"
                value={form.password_hash}
                onChange={onChange}
                required={!isEdit}
                fullWidth
                autoComplete="new-password"
              />
            )}
            <TextField
              select
              label="Role"
              name="application_user_role_id"
              value={form.application_user_role_id}
              onChange={onChange}
              disabled={isView}
              fullWidth
            >
              <MenuItem value="">No role</MenuItem>
              {roles.map((role) => (
                <MenuItem
                  key={role.application_user_role_id}
                  value={String(role.application_user_role_id)}
                >
                  {role.user_role_name || role.role_name}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.is_active)}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                  disabled={isView}
                />
              }
              label="Active"
            />
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => navigate("/users")}>
                Back
              </Button>
              {!isView && (
                <Button type="submit" variant="contained" disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
